import { inject } from '@angular/core';
import { CanMatchFn, GuardResult, MaybeAsync } from '@angular/router';
import { Persona } from '@core/config/persona.config';
import { WorkspaceType } from '@core/config/workspace-types.config';
import { AuthService } from '@core/services/auth.service';
import { WorkspaceService } from '@core/services/workspace.service';
import { map } from 'rxjs';

/**
 * canMatch predicates that disambiguate a single flat URL by the active
 * workspace. Because the workspace-type prefix was dropped from routes, several
 * paths (e.g. "", "assessments", "campaigns", "settings", "marketplace") map to
 * a different component depending on the current workspace. Listing the same
 * path multiple times with these guards lets the router pick the right one from
 * state instead of from the URL.
 *
 * canMatch runs during route recognition — before the shell's workspaceGuard
 * (canActivate) hydrates state — so on a cold reload the workspace may not be
 * restored yet. Each predicate therefore ensures the session is loaded and the
 * stored workspace is re-selected before reading it (loadSession() dedupes the
 * concurrent calls the multiple home/settings variants trigger).
 */
function resolve(read: (ws: WorkspaceService) => boolean): MaybeAsync<GuardResult> {
  const ws = inject(WorkspaceService);
  const auth = inject(AuthService);
  const check = (): boolean => {
    if (!ws.currentWorkspace()) ws.restoreFromStorage();
    return read(ws);
  };
  if (ws.currentWorkspace() || auth.currentUser() || !auth.hasToken()) {
    return check();
  }
  return auth.ensureSession().pipe(map(check));
}

/** Matches when the active workspace is one of the given types. */
export function typeMatch(...types: WorkspaceType[]): CanMatchFn {
  return () =>
    resolve((ws) => {
      const type = ws.currentType();
      return !!type && types.includes(type);
    });
}

/** Matches when the active workspace resolves to one of the given personas. */
export function personaMatch(...personas: Persona[]): CanMatchFn {
  return () =>
    resolve((ws) => {
      const persona = ws.currentPersona();
      return !!persona && personas.includes(persona);
    });
}
